/**
 * Canonical defaults for the Loan Lifecycle Subsystem (Issue #1388).
 *
 * Per-business default lists used by both:
 *   - The migration's per-business backfill
 *   - The service seed scripts (case-management, etc.) that bootstrap a
 *     freshly created business with a working lifecycle out of the box.
 *
 * Lender admins can edit, reorder, fork-per-product, and add-to these
 * defaults from the Control Center after seeding. Nothing here is
 * platform-canonical at runtime — the runtime authority is the per-business
 * `loan_stages` rows, not these constants.
 */

import { LoanStageCapability } from '../../enums/loan-stage-capability.enums';

// ============================================================================
// CANONICAL STAGES (8 — visible on the lender stepper)
// ============================================================================

export interface DefaultLoanStage {
  /** Stable key used for deterministic seed IDs and substage parent lookup. */
  key: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  order: number;
  capabilities: LoanStageCapability[];
  isInitial: boolean;
  isTerminal: boolean;
  slaHours?: number;
}

export const DEFAULT_LOAN_STAGES: readonly DefaultLoanStage[] = [
  {
    key: 'TO_REVIEW',
    name: 'To Review',
    description: 'New loan submissions awaiting initial triage.',
    color: '#94A3B8',
    icon: 'Inbox',
    order: 0,
    capabilities: [LoanStageCapability.IS_INITIAL, LoanStageCapability.TRIGGERS_INTAKE],
    isInitial: true,
    isTerminal: false,
    slaHours: 24,
  },
  {
    key: 'IN_PROGRESS',
    name: 'In Progress',
    description: 'Loan officer is actively gathering information and structuring the deal.',
    color: '#3B82F6',
    icon: 'Edit',
    order: 1,
    capabilities: [LoanStageCapability.TRIGGERS_REVIEW],
    isInitial: false,
    isTerminal: false,
    slaHours: 72,
  },
  {
    key: 'TERM_SHEET',
    name: 'Term Sheet',
    description: 'Term sheet drafted, issued, and awaiting borrower signature.',
    color: '#8B5CF6',
    icon: 'Description',
    order: 2,
    capabilities: [LoanStageCapability.TRIGGERS_TERM_SHEET],
    isInitial: false,
    isTerminal: false,
    slaHours: 120,
  },
  {
    key: 'UNDERWRITING',
    name: 'Underwriting',
    description: 'Credit review, appraisal, title, and credit memo drafting.',
    color: '#F59E0B',
    icon: 'FactCheck',
    order: 3,
    capabilities: [
      LoanStageCapability.TRIGGERS_UNDERWRITING,
      LoanStageCapability.TRIGGERS_CREDIT_PULL,
      LoanStageCapability.TRIGGERS_APPRAISAL,
      LoanStageCapability.TRIGGERS_TITLE_ORDER,
    ],
    isInitial: false,
    isTerminal: false,
    slaHours: 240,
  },
  {
    key: 'APPROVAL',
    name: 'Approval',
    description: 'Conditional approval and committee review.',
    color: '#10B981',
    icon: 'Verified',
    order: 4,
    capabilities: [LoanStageCapability.REQUIRES_COMMITTEE],
    isInitial: false,
    isTerminal: false,
    slaHours: 96,
  },
  {
    key: 'CLOSING',
    name: 'Closing',
    description: 'Clear-to-close, docs out, signing, and funding review.',
    color: '#06B6D4',
    icon: 'Gavel',
    order: 5,
    capabilities: [LoanStageCapability.TRIGGERS_CLOSING, LoanStageCapability.TRIGGERS_DOCS_SIGNED],
    isInitial: false,
    isTerminal: false,
    slaHours: 168,
  },
  {
    key: 'FUNDED',
    name: 'Funded',
    description: 'Money disbursed; post-close QC in flight.',
    color: '#22C55E',
    icon: 'AccountBalanceWallet',
    order: 6,
    capabilities: [LoanStageCapability.IS_FUNDED, LoanStageCapability.REQUIRES_QC],
    isInitial: false,
    isTerminal: false,
  },
  {
    key: 'SERVICING',
    name: 'Servicing',
    description: 'Active servicing window — payments, escrow, draws.',
    color: '#0EA5E9',
    icon: 'Repeat',
    order: 7,
    capabilities: [LoanStageCapability.IS_SERVICING],
    isInitial: false,
    isTerminal: false,
  },
  // Terminal stages — at least one IS_TERMINAL stage is required for graph
  // validation to pass. These three cover the common terminal outcomes
  // (decline, withdrawal, payoff). Lender admins can rename / add / remove
  // them from the Loan Stages page.
  {
    key: 'DECLINED',
    name: 'Declined',
    description: 'Loan declined. ECOA adverse-action notice required.',
    color: '#EF4444',
    icon: 'Block',
    order: 8,
    capabilities: [LoanStageCapability.IS_TERMINAL, LoanStageCapability.TERMINAL_DECLINED],
    isInitial: false,
    isTerminal: true,
  },
  {
    key: 'WITHDRAWN',
    name: 'Withdrawn',
    description: 'Borrower-initiated withdrawal before close.',
    color: '#94A3B8',
    icon: 'CallMissedOutgoing',
    order: 9,
    capabilities: [LoanStageCapability.IS_TERMINAL, LoanStageCapability.TERMINAL_WITHDRAWN],
    isInitial: false,
    isTerminal: true,
  },
  {
    key: 'PAID_OFF',
    name: 'Paid Off',
    description: 'Loan paid off in full from servicing.',
    color: '#22C55E',
    icon: 'CheckCircle',
    order: 10,
    capabilities: [LoanStageCapability.IS_TERMINAL, LoanStageCapability.TERMINAL_FUNDED],
    isInitial: false,
    isTerminal: true,
  },
];

// ============================================================================
// CANONICAL SUBSTAGES (30 — granular within each parent stage)
// ============================================================================

export interface DefaultLoanSubstage {
  parentStageKey: string;
  key: string;
  name: string;
  order: number;
  capabilities?: LoanStageCapability[];
}

export const DEFAULT_LOAN_SUBSTAGES: readonly DefaultLoanSubstage[] = [
  // TO_REVIEW
  { parentStageKey: 'TO_REVIEW', key: 'TO_REVIEW_NEW', name: 'New', order: 0 },
  { parentStageKey: 'TO_REVIEW', key: 'TO_REVIEW_ASSIGNED', name: 'Assigned', order: 1 },
  { parentStageKey: 'TO_REVIEW', key: 'TO_REVIEW_ESCALATED', name: 'Escalated', order: 2 },
  // IN_PROGRESS
  { parentStageKey: 'IN_PROGRESS', key: 'IN_PROGRESS_SIZING', name: 'Sizing', order: 0 },
  { parentStageKey: 'IN_PROGRESS', key: 'IN_PROGRESS_STRUCTURING', name: 'Structuring', order: 1 },
  { parentStageKey: 'IN_PROGRESS', key: 'IN_PROGRESS_AWAITING_INFO', name: 'Awaiting Info', order: 2 },
  // TERM_SHEET
  {
    parentStageKey: 'TERM_SHEET',
    key: 'TERM_SHEET_ISSUED',
    name: 'Issued',
    order: 0,
    capabilities: [LoanStageCapability.TRIGGERS_TERM_SHEET],
  },
  {
    parentStageKey: 'TERM_SHEET',
    key: 'TERM_SHEET_SIGNED',
    name: 'Signed',
    order: 1,
    capabilities: [LoanStageCapability.TRIGGERS_TERM_SHEET_SIGNED],
  },
  { parentStageKey: 'TERM_SHEET', key: 'TERM_SHEET_COUNTERED', name: 'Countered', order: 2 },
  // UNDERWRITING
  { parentStageKey: 'UNDERWRITING', key: 'UNDERWRITING_PROCESSING', name: 'Processing', order: 0 },
  { parentStageKey: 'UNDERWRITING', key: 'UNDERWRITING_IN_REVIEW', name: 'In Review', order: 1 },
  {
    parentStageKey: 'UNDERWRITING',
    key: 'UNDERWRITING_AWAITING_APPRAISAL',
    name: 'Awaiting Appraisal',
    order: 2,
    capabilities: [LoanStageCapability.TRIGGERS_APPRAISAL],
  },
  {
    parentStageKey: 'UNDERWRITING',
    key: 'UNDERWRITING_AWAITING_TITLE',
    name: 'Awaiting Title',
    order: 3,
    capabilities: [LoanStageCapability.TRIGGERS_TITLE_ORDER],
  },
  { parentStageKey: 'UNDERWRITING', key: 'UNDERWRITING_CREDIT_MEMO_DRAFTED', name: 'Credit Memo Drafted', order: 4 },
  { parentStageKey: 'UNDERWRITING', key: 'UNDERWRITING_SUSPENDED', name: 'Suspended', order: 5 },
  // APPROVAL
  { parentStageKey: 'APPROVAL', key: 'APPROVAL_CONDITIONAL', name: 'Conditional', order: 0 },
  {
    parentStageKey: 'APPROVAL',
    key: 'APPROVAL_COMMITTEE_REVIEW',
    name: 'Committee Review',
    order: 1,
    capabilities: [LoanStageCapability.REQUIRES_COMMITTEE],
  },
  { parentStageKey: 'APPROVAL', key: 'APPROVAL_COMMITTED', name: 'Committed', order: 2 },
  // CLOSING
  { parentStageKey: 'CLOSING', key: 'CLOSING_CLEAR_TO_CLOSE', name: 'Clear to Close', order: 0 },
  { parentStageKey: 'CLOSING', key: 'CLOSING_DOCS_OUT', name: 'Docs Out', order: 1 },
  {
    parentStageKey: 'CLOSING',
    key: 'CLOSING_DOCS_SIGNED',
    name: 'Docs Signed',
    order: 2,
    capabilities: [LoanStageCapability.TRIGGERS_DOCS_SIGNED],
  },
  { parentStageKey: 'CLOSING', key: 'CLOSING_FUNDING_REVIEW', name: 'Funding Review', order: 3 },
  // FUNDED
  { parentStageKey: 'FUNDED', key: 'FUNDED_INITIAL', name: 'Initial', order: 0 },
  {
    parentStageKey: 'FUNDED',
    key: 'FUNDED_POST_CLOSING_QC',
    name: 'Post-Closing QC',
    order: 1,
    capabilities: [LoanStageCapability.REQUIRES_QC],
  },
  // SERVICING
  { parentStageKey: 'SERVICING', key: 'SERVICING_CURRENT', name: 'Current', order: 0 },
  { parentStageKey: 'SERVICING', key: 'SERVICING_GRACE', name: 'Grace Period', order: 1 },
  { parentStageKey: 'SERVICING', key: 'SERVICING_DELINQUENT_30', name: 'Delinquent 30', order: 2 },
  { parentStageKey: 'SERVICING', key: 'SERVICING_DELINQUENT_60', name: 'Delinquent 60', order: 3 },
  { parentStageKey: 'SERVICING', key: 'SERVICING_DELINQUENT_90', name: 'Delinquent 90+', order: 4 },
  { parentStageKey: 'SERVICING', key: 'SERVICING_MATURED', name: 'Matured', order: 5 },
];

// ============================================================================
// CANONICAL TRANSITIONS (allowed forward moves between stages)
// ============================================================================

export interface DefaultLoanStageTransition {
  fromStageKey: string;
  toStageKey: string;
  isBackward?: boolean;
}

export const DEFAULT_LOAN_STAGE_TRANSITIONS: readonly DefaultLoanStageTransition[] = [
  // Forward path
  { fromStageKey: 'TO_REVIEW', toStageKey: 'IN_PROGRESS' },
  { fromStageKey: 'IN_PROGRESS', toStageKey: 'TERM_SHEET' },
  { fromStageKey: 'IN_PROGRESS', toStageKey: 'UNDERWRITING' }, // skip TS for lenders that price in UW
  { fromStageKey: 'TERM_SHEET', toStageKey: 'UNDERWRITING' },
  { fromStageKey: 'UNDERWRITING', toStageKey: 'APPROVAL' },
  { fromStageKey: 'APPROVAL', toStageKey: 'CLOSING' },
  { fromStageKey: 'CLOSING', toStageKey: 'FUNDED' },
  { fromStageKey: 'FUNDED', toStageKey: 'SERVICING' },
  // Common back-steps (admin-style revivals + re-pricing)
  { fromStageKey: 'TERM_SHEET', toStageKey: 'IN_PROGRESS', isBackward: true },
  { fromStageKey: 'UNDERWRITING', toStageKey: 'TERM_SHEET', isBackward: true },
  { fromStageKey: 'APPROVAL', toStageKey: 'UNDERWRITING', isBackward: true },
  { fromStageKey: 'CLOSING', toStageKey: 'APPROVAL', isBackward: true },
  // Loop-backs
  { fromStageKey: 'TERM_SHEET', toStageKey: 'TERM_SHEET' }, // re-issue
  { fromStageKey: 'UNDERWRITING', toStageKey: 'UNDERWRITING' }, // re-review
  // Decline + withdrawal exits — reachable from any non-terminal pipeline stage.
  { fromStageKey: 'TO_REVIEW', toStageKey: 'DECLINED' },
  { fromStageKey: 'IN_PROGRESS', toStageKey: 'DECLINED' },
  { fromStageKey: 'TERM_SHEET', toStageKey: 'DECLINED' },
  { fromStageKey: 'UNDERWRITING', toStageKey: 'DECLINED' },
  { fromStageKey: 'APPROVAL', toStageKey: 'DECLINED' },
  { fromStageKey: 'TO_REVIEW', toStageKey: 'WITHDRAWN' },
  { fromStageKey: 'IN_PROGRESS', toStageKey: 'WITHDRAWN' },
  { fromStageKey: 'TERM_SHEET', toStageKey: 'WITHDRAWN' },
  { fromStageKey: 'UNDERWRITING', toStageKey: 'WITHDRAWN' },
  { fromStageKey: 'APPROVAL', toStageKey: 'WITHDRAWN' },
  { fromStageKey: 'CLOSING', toStageKey: 'WITHDRAWN' },
  // Payoff — only from servicing.
  { fromStageKey: 'SERVICING', toStageKey: 'PAID_OFF' },
  // Admin "revive" backward edges from terminal stages so a mistakenly
  // declined / withdrawn loan can be brought back into the pipeline.
  { fromStageKey: 'DECLINED', toStageKey: 'IN_PROGRESS', isBackward: true },
  { fromStageKey: 'WITHDRAWN', toStageKey: 'IN_PROGRESS', isBackward: true },
];

// ============================================================================
// CANONICAL DATA POINTS (33 — collected during the loan lifecycle)
// ============================================================================

export interface DefaultLoanDataPoint {
  canonicalKey: string;
  displayName: string;
  category: string;
  valueType: 'STRING' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'CURRENCY' | 'DOCUMENT';
  caseFieldName?: string;
  /**
   * Distinguishes data fields satisfied by a Case column / suggestion
   * (DATA_POINT) from closing-checklist document slots fulfilled by an
   * uploaded attachment (DOCUMENT_SLOT). Defaults to DATA_POINT. See #1480.
   */
  kind?: 'DATA_POINT' | 'DOCUMENT_SLOT';
}

export const DEFAULT_LOAN_DATA_POINTS: readonly DefaultLoanDataPoint[] = [
  // BORROWER
  {
    canonicalKey: 'borrower_first_name',
    displayName: 'Borrower First Name',
    category: 'BORROWER',
    valueType: 'STRING',
  },
  { canonicalKey: 'borrower_last_name', displayName: 'Borrower Last Name', category: 'BORROWER', valueType: 'STRING' },
  { canonicalKey: 'borrower_email', displayName: 'Borrower Email', category: 'BORROWER', valueType: 'STRING' },
  { canonicalKey: 'borrower_phone', displayName: 'Borrower Phone', category: 'BORROWER', valueType: 'STRING' },
  { canonicalKey: 'borrower_address', displayName: 'Borrower Address', category: 'BORROWER', valueType: 'STRING' },
  { canonicalKey: 'borrower_dob', displayName: 'Borrower Date of Birth', category: 'BORROWER', valueType: 'DATE' },
  {
    canonicalKey: 'borrower_ssn_last4',
    displayName: 'Borrower SSN (Last 4)',
    category: 'BORROWER',
    valueType: 'STRING',
  },
  {
    canonicalKey: 'borrowing_entity_name',
    displayName: 'Borrowing Entity Name',
    category: 'BORROWER',
    valueType: 'STRING',
  },
  {
    canonicalKey: 'borrowing_entity_type',
    displayName: 'Borrowing Entity Type',
    category: 'BORROWER',
    valueType: 'STRING',
  },
  // PROPERTY
  {
    canonicalKey: 'property_address',
    displayName: 'Property Address',
    category: 'PROPERTY',
    valueType: 'STRING',
    caseFieldName: 'propertyAddress',
  },
  {
    canonicalKey: 'property_type',
    displayName: 'Property Type',
    category: 'PROPERTY',
    valueType: 'STRING',
    caseFieldName: 'propertyType',
  },
  {
    canonicalKey: 'purchase_price',
    displayName: 'Purchase Price',
    category: 'PROPERTY',
    valueType: 'CURRENCY',
    caseFieldName: 'purchasePrice',
  },
  {
    canonicalKey: 'arv',
    displayName: 'After-Repair Value',
    category: 'PROPERTY',
    valueType: 'CURRENCY',
    caseFieldName: 'arv',
  },
  {
    canonicalKey: 'rehab_budget',
    displayName: 'Rehab Budget',
    category: 'PROPERTY',
    valueType: 'CURRENCY',
    caseFieldName: 'rehabBudget',
  },
  { canonicalKey: 'as_is_value', displayName: 'As-Is Value', category: 'PROPERTY', valueType: 'CURRENCY' },
  { canonicalKey: 'units', displayName: 'Number of Units', category: 'PROPERTY', valueType: 'NUMBER' },
  { canonicalKey: 'square_footage', displayName: 'Square Footage', category: 'PROPERTY', valueType: 'NUMBER' },
  { canonicalKey: 'year_built', displayName: 'Year Built', category: 'PROPERTY', valueType: 'NUMBER' },
  // FINANCIAL
  { canonicalKey: 'credit_score', displayName: 'Credit Score', category: 'FINANCIAL', valueType: 'NUMBER' },
  {
    canonicalKey: 'requested_loan_amount',
    displayName: 'Requested Loan Amount',
    category: 'FINANCIAL',
    valueType: 'CURRENCY',
    caseFieldName: 'committedAmount',
  },
  {
    canonicalKey: 'ltv',
    displayName: 'Loan to Value (LTV)',
    category: 'FINANCIAL',
    valueType: 'NUMBER',
    caseFieldName: 'ltv',
  },
  {
    canonicalKey: 'ltc',
    displayName: 'Loan to Cost (LTC)',
    category: 'FINANCIAL',
    valueType: 'NUMBER',
    caseFieldName: 'ltc',
  },
  { canonicalKey: 'dscr', displayName: 'DSCR Ratio', category: 'FINANCIAL', valueType: 'NUMBER' },
  { canonicalKey: 'monthly_rent', displayName: 'Monthly Rent', category: 'FINANCIAL', valueType: 'CURRENCY' },
  {
    canonicalKey: 'interest_rate',
    displayName: 'Interest Rate',
    category: 'FINANCIAL',
    valueType: 'NUMBER',
    caseFieldName: 'interestRate',
  },
  {
    canonicalKey: 'loan_term_months',
    displayName: 'Loan Term (Months)',
    category: 'FINANCIAL',
    valueType: 'NUMBER',
    caseFieldName: 'loanTermMonths',
  },
  {
    canonicalKey: 'origination_points',
    displayName: 'Origination Points',
    category: 'FINANCIAL',
    valueType: 'NUMBER',
    caseFieldName: 'originationPoints',
  },
  // EMPLOYMENT
  { canonicalKey: 'employment_status', displayName: 'Employment Status', category: 'EMPLOYMENT', valueType: 'STRING' },
  { canonicalKey: 'annual_income', displayName: 'Annual Income', category: 'EMPLOYMENT', valueType: 'CURRENCY' },
  // LEGAL
  { canonicalKey: 'title_report', displayName: 'Title Report Received', category: 'LEGAL', valueType: 'BOOLEAN' },
  { canonicalKey: 'appraisal_received', displayName: 'Appraisal Received', category: 'LEGAL', valueType: 'BOOLEAN' },
  { canonicalKey: 'insurance_binder', displayName: 'Insurance Binder', category: 'LEGAL', valueType: 'BOOLEAN' },
  { canonicalKey: 'flood_certificate', displayName: 'Flood Certificate', category: 'LEGAL', valueType: 'BOOLEAN' },

  // ==========================================================================
  // CLOSING CHECKLIST (Issue #1480) — kind=DOCUMENT_SLOT, 37 anonymized items
  // organized into general / borrower / property / gc_builder / post_closing.
  // Wired to the CLOSING stage by DEFAULT_LOAN_STAGE_REQUIREMENTS below with
  // per-product (fix_and_flip / ground_up / bridge) variations + applicability
  // rules. DSCR / commercial / multifamily columns are intentionally blank;
  // tenants populate via Control Tower per epic #1460.
  // ==========================================================================

  // -- general --------------------------------------------------------------
  {
    canonicalKey: 'closing.term_sheet',
    displayName: 'Term Sheet',
    category: 'general',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.assumption_modification',
    displayName: 'Assumption / Modification / Extension / Consolidation',
    category: 'general',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.title_policy',
    displayName: 'Title Policy or Title Commitment',
    category: 'general',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },

  // -- borrower -------------------------------------------------------------
  {
    canonicalKey: 'closing.photo_id_guarantor',
    displayName: 'Photo ID — all guarantors',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.credit_report',
    displayName: 'Credit Report',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.background_report',
    displayName: 'Background Report',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.ofac_check',
    displayName: 'OFAC Check',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.borrower_experience',
    displayName: 'Borrower Experience / REO Sheet',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.guarantor_pfs',
    displayName: 'Guarantor PFS',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.bank_statements_2mo',
    displayName: '2-Month Bank Statements',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.articles_of_formation',
    displayName: 'Articles of Formation / Organization',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.operating_agreement',
    displayName: 'Operating Agreement',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.cert_good_standing',
    displayName: 'Certificate of Good Standing',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.ein_w9_tin',
    displayName: 'EIN / W-9 / TIN',
    category: 'borrower',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },

  // -- property -------------------------------------------------------------
  {
    canonicalKey: 'closing.scope_of_work',
    displayName: 'Scope of Work / Budget',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.appraisal_or_bpo',
    displayName: 'Appraisal or BPO',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.verification_of_mortgage',
    displayName: 'Verification of Mortgage',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.purchase_contract',
    displayName: 'Purchase Documentation / Contract',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.transferring_deed',
    displayName: 'Transferring Deed (Warranty Deed)',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.flood_certification',
    displayName: 'Flood Certification',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.evidence_of_insurance',
    displayName: 'Evidence of Insurance',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.permits',
    displayName: 'Permits / Permit Status',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.cert_of_occupancy',
    displayName: 'Certificate of Occupancy / Completion',
    category: 'property',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },

  // -- gc_builder -----------------------------------------------------------
  {
    canonicalKey: 'closing.gc_experience',
    displayName: 'GC / Builder Experience',
    category: 'gc_builder',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.gc_license',
    displayName: 'GC / Builder License',
    category: 'gc_builder',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.gc_gl_insurance',
    displayName: 'GC GL Insurance (borrower + lender as Additional Insured)',
    category: 'gc_builder',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.gc_workers_comp',
    displayName: 'GC Workers Comp (borrower + lender as Additional Insured)',
    category: 'gc_builder',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },

  // -- post_closing ---------------------------------------------------------
  {
    canonicalKey: 'closing.hud_1',
    displayName: 'Certified Settlement Statement (HUD-1)',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.promissory_note',
    displayName: 'Promissory Note + intervening endorsements',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.loan_agreement',
    displayName: 'Loan Agreement',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.deed_of_trust',
    displayName: 'Deed of Trust / Mortgage (recorded or certified)',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.deed_unrecorded',
    displayName: 'Deed — Unrecorded',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.deed_recorded',
    displayName: 'Deed — Recorded',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.original_guarantee',
    displayName: 'Original Guarantee(s) — all guarantors',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.bpa_non_owner_occ_cert',
    displayName: 'Business Purpose Affidavit / Non-Owner-Occupied Cert',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.investor_loan_tape',
    displayName: 'Investor / Capital Markets Loan Tape',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
  {
    canonicalKey: 'closing.allonge',
    displayName: 'Allonge',
    category: 'post_closing',
    valueType: 'DOCUMENT',
    kind: 'DOCUMENT_SLOT',
  },
];

// ============================================================================
// DEFAULT REQUIREMENTS — which data points are REQUIRED at each stage
// ============================================================================

export type DefaultRequirementLevel = 'REQUIRED' | 'OPTIONAL';

export interface DefaultLoanStageRequirement {
  stageKey: string;
  dataPointKey: string;
  requirement: DefaultRequirementLevel;
  /**
   * Optional product-scoping. NULL/undefined = applies to every product
   * in the business; setting it overrides the default for that product
   * only (matches LoanStageRequirement.loanProductId semantics).
   */
  productCode?: string;
  /**
   * Optional JSON-Logic-style rule narrowing when the requirement applies
   * (e.g. `borrowerTier in (2,3) OR loanAmount > 1000000`). See #1480.
   * Stored as-is into LoanStageRequirement.applicabilityRule (JSONB).
   */
  applicabilityRule?: unknown;
  /** Human-readable rationale shown in UI tooltips. */
  applicabilityNote?: string;
}

/**
 * Each entry maps a data point to the EARLIEST stage at which it must be
 * collected. Generates a baseline matrix of ~50 requirement rows. Lender
 * admins refine via the Stage Requirements matrix UI.
 */
export const DEFAULT_LOAN_STAGE_REQUIREMENTS: readonly DefaultLoanStageRequirement[] = [
  // IN_PROGRESS — basic borrower + property identification
  { stageKey: 'IN_PROGRESS', dataPointKey: 'borrower_first_name', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'borrower_last_name', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'borrower_email', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'borrower_phone', requirement: 'OPTIONAL' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'property_address', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'property_type', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'requested_loan_amount', requirement: 'REQUIRED' },
  { stageKey: 'IN_PROGRESS', dataPointKey: 'purchase_price', requirement: 'REQUIRED' },

  // TERM_SHEET — pricing inputs
  { stageKey: 'TERM_SHEET', dataPointKey: 'arv', requirement: 'OPTIONAL' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'rehab_budget', requirement: 'OPTIONAL' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'credit_score', requirement: 'REQUIRED' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'ltv', requirement: 'REQUIRED' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'interest_rate', requirement: 'REQUIRED' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'loan_term_months', requirement: 'REQUIRED' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'origination_points', requirement: 'OPTIONAL' },
  { stageKey: 'TERM_SHEET', dataPointKey: 'borrower_address', requirement: 'REQUIRED' },

  // UNDERWRITING — full credit + property file
  { stageKey: 'UNDERWRITING', dataPointKey: 'borrower_dob', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'borrower_ssn_last4', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'borrowing_entity_name', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'borrowing_entity_type', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'arv', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'as_is_value', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'units', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'square_footage', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'year_built', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'dscr', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'monthly_rent', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'employment_status', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'annual_income', requirement: 'OPTIONAL' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'appraisal_received', requirement: 'REQUIRED' },
  { stageKey: 'UNDERWRITING', dataPointKey: 'title_report', requirement: 'REQUIRED' },

  // APPROVAL — closing prerequisites
  { stageKey: 'APPROVAL', dataPointKey: 'insurance_binder', requirement: 'REQUIRED' },
  { stageKey: 'APPROVAL', dataPointKey: 'flood_certificate', requirement: 'OPTIONAL' },

  // CLOSING — final docs
  { stageKey: 'CLOSING', dataPointKey: 'ltc', requirement: 'OPTIONAL' },

  // ==========================================================================
  // CLOSING CHECKLIST (Issue #1480) — kind=DOCUMENT_SLOT items wired to the
  // CLOSING stage. Items where all three deal types (fix_and_flip, ground_up,
  // bridge) agree get a single row with productCode=null (applies to every
  // product the business has on its catalog). Items that differ by product
  // are seeded once per product. Conditional items carry an applicabilityRule
  // evaluated by `evaluateApplicabilityRule` in shared-types/utils.
  // ==========================================================================

  // -- general (1-3): all three deal types REQUIRED, no conditions ---------
  { stageKey: 'CLOSING', dataPointKey: 'closing.term_sheet', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.assumption_modification', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.title_policy', requirement: 'REQUIRED' },

  // -- borrower (4-14): mostly aligned across products ----------------------
  { stageKey: 'CLOSING', dataPointKey: 'closing.photo_id_guarantor', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.credit_report', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.background_report', requirement: 'REQUIRED' },
  // OFAC: triggered when background report flags a name match
  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.ofac_check',
    requirement: 'OPTIONAL',
    applicabilityRule: { field: 'backgroundReportFlagged', op: 'eq', value: true },
    applicabilityNote: 'Recommended when the background report flags a name match.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.borrower_experience', requirement: 'REQUIRED' },
  // Guarantor PFS: large-loan or weaker-tier guidance
  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.guarantor_pfs',
    requirement: 'OPTIONAL',
    applicabilityRule: {
      any: [
        { field: 'loanAmount', op: 'gt', value: 1_000_000 },
        { field: 'borrowerTier', op: 'in', value: [2, 3] },
      ],
    },
    applicabilityNote: 'Recommended for loans over $1M or guarantors at Tier 2/3.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.bank_statements_2mo', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.articles_of_formation', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.operating_agreement', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.cert_good_standing', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.ein_w9_tin', requirement: 'REQUIRED' },

  // -- property (15-23): some product-scoped variations --------------------
  // Scope of Work: REQUIRED for F&F + Ground Up; OPTIONAL for Bridge (often no rehab).
  { stageKey: 'CLOSING', dataPointKey: 'closing.scope_of_work', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.scope_of_work', requirement: 'OPTIONAL', productCode: 'bridge' },

  { stageKey: 'CLOSING', dataPointKey: 'closing.appraisal_or_bpo', requirement: 'REQUIRED' },
  // Verification of Mortgage: only for refinances
  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.verification_of_mortgage',
    requirement: 'OPTIONAL',
    applicabilityRule: { field: 'loanPurpose', op: 'eq', value: 'refinance' },
    applicabilityNote: 'Required only on refinances — supplied by current lender.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.purchase_contract', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.transferring_deed', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.flood_certification', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.evidence_of_insurance', requirement: 'REQUIRED' },

  // Permits: F&F=OPTIONAL (with applicabilityRule that flags it on rehab budgets >$300K),
  // Ground Up=REQUIRED, Bridge=OPTIONAL.
  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.permits',
    requirement: 'OPTIONAL',
    productCode: 'fix_and_flip',
    applicabilityRule: { field: 'scopeAmount', op: 'gt', value: 300_000 },
    applicabilityNote: 'Recommended on F&F deals with rehab budgets above $300K.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.permits', requirement: 'REQUIRED', productCode: 'ground_up' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.permits', requirement: 'OPTIONAL', productCode: 'bridge' },

  { stageKey: 'CLOSING', dataPointKey: 'closing.cert_of_occupancy', requirement: 'OPTIONAL' },

  // -- gc_builder (24-27): same product matrix as Permits ------------------
  // Pattern across all four GC items: F&F OPTIONAL with an applicabilityRule
  // that flags the row on scopeAmount>300K, Ground Up REQUIRED, Bridge OPTIONAL.
  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.gc_experience',
    requirement: 'OPTIONAL',
    productCode: 'fix_and_flip',
    applicabilityRule: { field: 'scopeAmount', op: 'gt', value: 300_000 },
    applicabilityNote: 'Recommended on F&F deals with rehab budgets above $300K.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_experience', requirement: 'REQUIRED', productCode: 'ground_up' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_experience', requirement: 'OPTIONAL', productCode: 'bridge' },

  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.gc_license',
    requirement: 'OPTIONAL',
    productCode: 'fix_and_flip',
    applicabilityRule: { field: 'scopeAmount', op: 'gt', value: 300_000 },
    applicabilityNote: 'Recommended on F&F deals with rehab budgets above $300K.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_license', requirement: 'REQUIRED', productCode: 'ground_up' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_license', requirement: 'OPTIONAL', productCode: 'bridge' },

  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.gc_gl_insurance',
    requirement: 'OPTIONAL',
    productCode: 'fix_and_flip',
    applicabilityRule: { field: 'scopeAmount', op: 'gt', value: 300_000 },
    applicabilityNote: 'Recommended on F&F deals with rehab budgets above $300K.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_gl_insurance', requirement: 'REQUIRED', productCode: 'ground_up' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_gl_insurance', requirement: 'OPTIONAL', productCode: 'bridge' },

  {
    stageKey: 'CLOSING',
    dataPointKey: 'closing.gc_workers_comp',
    requirement: 'OPTIONAL',
    productCode: 'fix_and_flip',
    applicabilityRule: { field: 'scopeAmount', op: 'gt', value: 300_000 },
    applicabilityNote: 'Recommended on F&F deals with rehab budgets above $300K.',
  },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_workers_comp', requirement: 'REQUIRED', productCode: 'ground_up' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.gc_workers_comp', requirement: 'OPTIONAL', productCode: 'bridge' },

  // -- post_closing (28-37): all three REQUIRED except Investor Loan Tape --
  { stageKey: 'CLOSING', dataPointKey: 'closing.hud_1', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.promissory_note', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.loan_agreement', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.deed_of_trust', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.deed_unrecorded', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.deed_recorded', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.original_guarantee', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.bpa_non_owner_occ_cert', requirement: 'REQUIRED' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.investor_loan_tape', requirement: 'OPTIONAL' },
  { stageKey: 'CLOSING', dataPointKey: 'closing.allonge', requirement: 'REQUIRED' },
];

// ============================================================================
// STARTER LOAN PRODUCTS (6)
// ============================================================================

export interface DefaultLoanProduct {
  code: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  sortOrder: number;
}

export const DEFAULT_LOAN_PRODUCTS: readonly DefaultLoanProduct[] = [
  {
    code: 'bridge',
    name: 'Bridge Loan',
    description: 'Short-term financing to bridge the gap between purchase and long-term financing.',
    category: 'bridge',
    icon: 'Bolt',
    color: '#F59E0B',
    sortOrder: 0,
  },
  {
    code: 'fix_and_flip',
    name: 'Fix & Flip',
    description: 'Short-term loan for purchase + rehab + resale of single-family residential property.',
    category: 'fix_and_flip',
    icon: 'HomeRepairService',
    color: '#EF4444',
    sortOrder: 1,
  },
  {
    code: 'dscr',
    name: 'DSCR Rental',
    description: 'Long-term rental loan underwritten on property cash flow rather than personal income.',
    category: 'dscr',
    icon: 'Apartment',
    color: '#10B981',
    sortOrder: 2,
  },
  {
    code: 'ground_up',
    name: 'Ground-Up Construction',
    description: 'Construction-to-perm financing for new builds.',
    category: 'ground_up',
    icon: 'Construction',
    color: '#06B6D4',
    sortOrder: 3,
  },
  {
    code: 'commercial',
    name: 'Commercial',
    description: 'Mixed-use, retail, office, and industrial commercial real estate loans.',
    category: 'commercial',
    icon: 'Business',
    color: '#8B5CF6',
    sortOrder: 4,
  },
  {
    code: 'multifamily',
    name: 'Multifamily',
    description: 'Loans on 5+ unit residential properties.',
    category: 'multifamily',
    icon: 'Domain',
    color: '#3B82F6',
    sortOrder: 5,
  },
];

// ============================================================================
// CANONICAL BORROWER TIER RULES (Issue #1469)
// ============================================================================
//
// Per-business rules that classify a borrower into Tier 1, 2, or 3 based on
// underwriting signals (credit score, trailing-36-month exit count, liquid
// reserves). Evaluated in priority order — lowest first; first match wins.
//
// Within a single rule, conditions are combined with AND — every populated
// condition must hold for the rule to fire. To express OR semantics, seed
// multiple rules (one per branch) at the same priority. Defaults below use
// AND throughout: a Tier 2 borrower must hit ALL of FICO 680+, 2+ exits,
// $50K+ reserves to land on Tier 2; missing one falls through to Tier 3.
//
// Default rule set seeded per business at creation; lender admins can edit,
// reorder, add, or deactivate rules from the Control Center. The catch-all
// (priority 100) MUST always be present so every borrower lands on a tier
// (otherwise the classifier returns Tier 3 by convention).
//
// Tiers feed pricing rate adjustments, underwriting LTV caps + reserve
// requirements, and the closing-checklist guarantor-PFS rule.

export interface DefaultBorrowerTierRule {
  /** Inclusive lower bound on FICO; undefined = no lower bound. */
  creditScoreMin?: number;
  /** Exclusive upper bound on FICO; undefined = no upper bound. */
  creditScoreMax?: number;
  /** Minimum trailing-36-month exit count to qualify. */
  exitCount36moMin: number;
  /** Minimum liquid reserves in dollars to qualify; undefined = no minimum. */
  liquidReservesMin?: number;
  /** Resulting tier when ALL above conditions match (1, 2, or 3). */
  tier: 1 | 2 | 3;
  /** Lower priority is checked first. Catch-all = highest priority value. */
  priority: number;
}

export const DEFAULT_BORROWER_TIER_RULES: readonly DefaultBorrowerTierRule[] = [
  // Tier 1 — strong: 720+ FICO AND 5+ exits AND $250K+ reserves (all required)
  {
    creditScoreMin: 720,
    exitCount36moMin: 5,
    liquidReservesMin: 250_000,
    tier: 1,
    priority: 0,
  },
  // Tier 2 — moderate: 680+ FICO AND 2+ exits AND $50K+ reserves (all required)
  {
    creditScoreMin: 680,
    exitCount36moMin: 2,
    liquidReservesMin: 50_000,
    tier: 2,
    priority: 10,
  },
  // Tier 3 — default catch-all (no requirements; always matches)
  {
    exitCount36moMin: 0,
    tier: 3,
    priority: 100,
  },
];
